/**
 * AI 画图对话流程的纯逻辑 (移植自旧版 index.html sendImageMessage):
 *   parse-image-request (带最近 6 条对话) → 缺尺寸则反问 → generate-image → 结果卡片
 */
import type { GenerateImageResponse, ParseImageResponse } from "@/features/quote/api";

export type ImageMsgKind = "text" | "pending" | "result" | "error" | "credits";

export interface ImageResult {
  src: string;
  /** 像素尺寸, 如 "2048x2048" */
  size: string;
  prompt: string;
}

export interface ImageMsg {
  id: string;
  role: "user" | "assistant";
  kind: ImageMsgKind;
  content: string;
  results?: ImageResult[];
  /** 计入发给 parse-image-request 的对话历史时的文本; undefined 表示不计入 */
  history?: string;
}

export const IMAGE_HISTORY_LIMIT = 6;

/** 取最近 N 条计入历史的消息 (不含本次输入: 后端会把 text 自己追加为最后一条 user 消息) */
export function buildImageHistory(
  msgs: ImageMsg[],
  limit = IMAGE_HISTORY_LIMIT,
): Array<{ role: "user" | "assistant"; content: string }> {
  return msgs
    .filter((m) => m.history !== undefined)
    .map((m) => ({ role: m.role, content: m.history as string }))
    .slice(-limit);
}

export const DEFAULT_ASK_SIZE = "请问您需要什么尺寸？（例如：180cm×50cm）";

export type ParseOutcome =
  | { kind: "error"; message: string }
  | { kind: "ask"; message: string; prompt: string }
  | { kind: "generate"; prompt: string; width_cm: number; height_cm: number };

/** 解释 /api/parse-image-request 的返回 */
export function interpretParse(resp: ParseImageResponse, userText: string): ParseOutcome {
  if (!resp || !resp.success) {
    return { kind: "error", message: resp?.error || "解析失败，请重新描述" };
  }
  const w = Number(resp.width_cm);
  const h = Number(resp.height_cm);
  if (resp.needs_size || !(w > 0) || !(h > 0)) {
    return {
      kind: "ask",
      message: resp.ask_message || DEFAULT_ASK_SIZE,
      prompt: resp.prompt || userText,
    };
  }
  return { kind: "generate", prompt: resp.prompt || userText, width_cm: w, height_cm: h };
}

export function sizeHint(widthCm: number, heightCm: number): string {
  return `${widthCm}cm × ${heightCm}cm`;
}

/** 图片地址: 有 url 用 url, 否则 b64_json 转 data URI */
export function imageSrc(img: { url?: string; b64_json?: string }): string | null {
  if (img.url) return img.url;
  if (img.b64_json) return `data:image/jpeg;base64,${img.b64_json}`;
  return null;
}

export type GenerateOutcome =
  | { kind: "error"; message: string }
  | { kind: "result"; results: ImageResult[]; history: string };

export function interpretGenerate(resp: GenerateImageResponse, prompt: string): GenerateOutcome {
  if (!resp || !resp.success) {
    return { kind: "error", message: `图片生成失败：${resp?.error || "未知错误"}` };
  }
  const size = resp.size || "2048x2048";
  const results = (resp.images ?? [])
    .map(imageSrc)
    .filter((s): s is string => !!s)
    .map((src) => ({ src, size, prompt }));
  if (results.length === 0) {
    return { kind: "error", message: "图片生成失败：未返回图片" };
  }
  return { kind: "result", results, history: `[图片已生成，像素：${size}]` };
}

export function truncatePrompt(p: string, max = 40): string {
  return p.length > max ? `${p.slice(0, max)}...` : p;
}
